import { Injectable } from '@angular/core';
import { ApplicationInsights, SeverityLevel, IEventTelemetry, ITraceTelemetry } from '@microsoft/applicationinsights-web';
import { AppConfigService } from './app-config.service'

@Injectable({
  providedIn: 'root'
})
export class LoggingService {
  private appInsights: ApplicationInsights;

  constructor(private appConfigService: AppConfigService) {
    if (this.appConfigService.loaded) {
      this.initAppInsights();
    }
  }

  private initAppInsights(): void {
    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: this.appConfigService.GetAIInstrKey,
        enableAutoRouteTracking: true,
        enableAjaxErrorStatusText: true,
        disableFetchTracking: false,
        disableAjaxTracking: false,
        autoTrackPageVisitTime: true
      }
    });
    this.appInsights.loadAppInsights();
  }

  private get ai(): ApplicationInsights | null {
    if (!this.appConfigService.loaded) return null;
    if (!this.appInsights) this.initAppInsights();
    return this.appInsights;
  }

  setUserContext(userId: string, accountId?: string): void {
    this.ai?.setAuthenticatedUserContext(userId, accountId, true);
  }

  clearUserContext(): void {
    this.ai?.clearAuthenticatedUserContext();
  }

  logPageView(name?: string, url?: string): void {
    this.ai?.trackPageView({ name, uri: url });
  }

  logEvent(name: string, properties?: { [key: string]: any }): void {
    this.ai?.trackEvent({ name } as IEventTelemetry, properties);
  }

  logMetric(name: string, average: number, properties?: { [key: string]: any }): void {
    this.ai?.trackMetric({ name, average }, properties);
  }

  logException(exception: Error | any, severityLevel: SeverityLevel = SeverityLevel.Error): void {
    const err = exception instanceof Error ? exception : new Error(String(exception));
    this.ai?.trackException({ exception: err, severityLevel });
  }

  logTrace(message: string, properties?: { [key: string]: any }, severityLevel: SeverityLevel = SeverityLevel.Information): void {
    const msg = message || 'No request data available.';
    this.ai?.trackTrace({ message: msg, severityLevel } as ITraceTelemetry, properties);
  }

  logWarning(message: string, properties?: { [key: string]: any }): void {
    this.logTrace(message, properties, SeverityLevel.Warning);
  }

  logError(message: string, properties?: { [key: string]: any }): void {
    this.logTrace(message, properties, SeverityLevel.Error);
  }

  /**
   * Logs an outbound HTTP dependency (request + response) as an AppInsights dependency telemetry.
   * @param method   HTTP verb (GET, POST, …)
   * @param url      Full request URL
   * @param duration Round-trip duration in milliseconds
   * @param status   HTTP response status code
   * @param success  Whether the call succeeded (2xx)
   * @param properties Extra key/value pairs to attach
   */
  logHttpDependency(
    method: string,
    url: string,
    duration: number,
    status: number,
    success: boolean,
    properties?: { [key: string]: any }
  ): void {
    const requestBody = properties?.['request.body'];
    const responseBody = properties?.['response.body'];

    const dependencyData: { [key: string]: any } = {
      id: `${method}-${Date.now()}`,
      name: `${method} ${url}`,
      duration,
      success,
      responseCode: status,
      type: 'HTTP',
      target: url,
      data: requestBody ? `${url} | Req: ${requestBody}` : url,
      properties: {
        ...properties,
        ...(responseBody && { 'response.body': responseBody })
      }
    };

    this.ai?.trackDependencyData(dependencyData as any);
  }
}
